import { LoadingButton } from '@mui/lab';
import { Box, Container, Divider, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { array, mixed, number, object, ObjectSchema, string } from 'yup';
import ClientOnboardingApi from '../../../../apis/ClientOnboardingApi';
import CoursesApi from '../../../../apis/CoursesApi';
import FormikCheckboxGroup from '../../../../components/FormikCheckboxGroup/FormikCheckboxGroup';
import FormikEnumSelect from '../../../../components/FormikEnumSelect/FormikEnumSelect';
import FormikListField from '../../../../components/FormikListField/FormikListField';
import FormikTextField from '../../../../components/FormikTextField/FormikTextField';
import LoadingCircle from '../../../../components/LoadingCircle/LoadingCircle';
import { Month } from '../../../../generated-types/month';
import { OrgSetupDto } from '../../../../generated-types/orgSetupDto';
import useApi from '../../../../lib/useApi';
import useSharedAuthLib from '../../../../lib/useSharedAuthLib';
import CommonStepProps from '../../lib/CommonStepProps';

/**
 * A component that guides the user through the remaining steps to setup an organization
 */
const OrganizationStep = ({ config, showError }: CommonStepProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [orgSetupDto, setOrgSetupDto] = useState<OrgSetupDto>({
    normalOrgName: '',
    yearlyLapseMonth: Month.January,
    yearlyLapseDay: 1,
    managerEmails: [],
    requiredCourses: [],
  });
  const [courseIdNameMapping, setCourseIdNameMapping] = useState<
    Record<string, string>
  >({});
  const getDaysInMonth = (
    month: Month,
    year: number = new Date().getFullYear()
  ) => {
    return new Date(year, month, 0).getDate();
  };
  const onboardingApi = useApi(ClientOnboardingApi);
  const courseApi = useApi(CoursesApi);
  const authLib = useSharedAuthLib();

  const schema: ObjectSchema<OrgSetupDto> = object({
    normalOrgName: string()
      .max(
        config.normalOrgNameMax,
        `Normal organization name cannot be longer than ${config.normalOrgNameMax} characters.`
      )
      .min(
        config.normalOrgNameMin,
        `Normal organization name must be at least ${config.normalOrgNameMin} characters.`
      )
      .defined('Required'),
    yearlyLapseMonth: mixed<Month>()
      .oneOf(
        Object.values(Month).filter(
          (value): value is Month => typeof value === 'number'
        )
      )
      .required('Month is required'),
    yearlyLapseDay: number()
      .required('Day is required')
      .test(
        'isValidDay',
        'Invalid day for the selected month',
        function (value, context) {
          const month = context.parent.yearlyLapseMonth;
          if (month != undefined && value != undefined) {
            const maxDays = getDaysInMonth(month);
            return value >= 1 && value <= maxDays;
          }
          return false;
        }
      ),
    managerEmails: array()
      .max(config.managerEmailsMax, 'You cannot add more than 10 managers.')
      .of(string().email('Invalid email address').required('Email is required'))
      .required('Emails are required'),
    requiredCourses: array().of(string().required()).required(),
  });

  const formik = useFormik<OrgSetupDto>({
    initialValues: orgSetupDto,
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: (val) => {
      onboardingApi
        .CompleteOrgSetup(val)
        .then(authLib.SetToken)
        .catch(showError);
    },
  });

  useEffect(() => {
    setIsLoading(true);
    const initialLoad = async () => {
      // Since payment has been received, we can get the org setup
      const orgSetupRequest = onboardingApi.GetOrgSetup();
      const courseIdNameMappingRequest = courseApi.GetIdNameMapping();

      setOrgSetupDto(await orgSetupRequest);
      setCourseIdNameMapping(await courseIdNameMappingRequest);
    };

    initialLoad()
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [onboardingApi, courseApi, showError]);

  return (
    <Container
      maxWidth="md"
      sx={{ paddingBottom: '2em', position: 'relative' }}
    >
      <form onSubmit={formik.handleSubmit}>
        <FormikTextField
          formik={formik}
          fullWidth
          label="Normal Organization Name"
          name="normalOrgName"
          helpText='E.g. "Disney", instead of "The Walt Disney Company"'
          sx={{
            marginBottom: '1em',
          }}
        />
        <Box>
          <Box marginBottom=".6em">
            <Typography>Yearly lapse date</Typography>
            <Typography variant="caption">
              This is the yearly day that your members&apos; course progress
              will be reset.
            </Typography>
          </Box>
          <Box display="flex" gap="1em">
            <FormikEnumSelect
              formik={formik}
              enum={Month}
              label="Month"
              name="yearlyLapseMonth"
              sx={{
                width: '50%',
              }}
            />
            <FormikTextField
              formik={formik}
              label="Day"
              name="yearlyLapseDay"
              sx={{
                width: '50%',
              }}
            />
          </Box>
        </Box>
        <Box marginBottom="1em">
          <Typography>Required Training</Typography>
          <Typography variant="caption">
            Select the training that should be required for your organization.
            Unselected training can still be optionally completed by your
            members.
          </Typography>
          <FormikCheckboxGroup
            formik={formik}
            name="requiredCourses"
            options={courseIdNameMapping}
          />
        </Box>
        <Box>
          <Box marginBottom=".6em">
            <Typography>Manager Emails</Typography>
            <Typography variant="caption">
              These people will have access to the manager dashboard for the
              training. They will be able to invite members and monitor their
              progress. You will be automatically added as a manager.
            </Typography>
            <Box marginTop="1em">
              <FormikListField
                formik={formik}
                name="managerEmails"
                itemName="email"
              />
            </Box>
          </Box>
        </Box>
        <Divider
          sx={{
            marginY: '1.5em',
          }}
        />
        <Box display="flex" justifyContent="center">
          <LoadingButton
            loading={formik.isSubmitting}
            type="submit"
            variant="contained"
          >
            Complete Onboarding
          </LoadingButton>
        </Box>
      </form>
      <LoadingCircle isLoading={isLoading} blurEdges />
    </Container>
  );
};

export default OrganizationStep;
